import { ActionIcon, Button, Card, Input } from "@common/components";
import { useMantineTheme } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { PauseRounded, PlayArrowRounded } from "@mui/icons-material";
import { Mixdown } from "@server/entities";
import { Howl } from "howler";
import React, { useEffect, useState } from "react";
import { ChevronLeft } from "tabler-icons-react";

import { WaveformLane } from "../../../../../../common/components/waveform/WaveformLane";
import { formatElapsedOverTotal } from "../../../../../../common/components/waveform/waveform-geometry";
import { stemHeight } from "../../../../../stemviewer/helpers/constants";

export type MixdownCompare = Partial<Mixdown> & {
  howl?: Howl;
  selected?: boolean;
};

interface MixdownCompareVersionsProps {
  mixdowns: MixdownCompare[];
  onClose: () => void;
}

export const MixdownCompareVersions: React.FC<MixdownCompareVersionsProps> = ({
  mixdowns: _mixdowns,
  onClose,
}) => {
  const [mixdowns, setMixdowns] = useState<Array<MixdownCompare>>(_mixdowns);
  const [playing, setPlaying] = useState(false);
  const [currTime, setCurrTime] = useState(0);
  const duration = Math.max(
    ...mixdowns.map((mixdown) => mixdown.duration || 0)
  );

  // Play or pause the audio with spacebar
  useHotkeys([
    [
      "space",
      (event) => {
        event.preventDefault();
        handlePlayPause();
      },
    ],
  ]);

  // Unload howls on exit
  useEffect(() => {
    return () => {
      mixdowns.forEach((mixdown) => mixdown.howl?.unload());
    };
  }, []);

  // Poll the transport at 200ms while playing — matching the global Player
  // context's own update rate (PlayerContext.tsx) — so the playhead advances
  // in the same visibly-continuous steps as the other two player surfaces.
  // The old 500ms rate only looked smooth because the (now-removed)
  // progress-fill div eased between polls; requirement 5 forbids restoring
  // that easing, so the fix is to poll more often instead.
  useEffect(() => {
    if (!playing) return;

    const interval = setInterval(() => {
      const longestMixdown =
        (mixdowns[0].duration || 0) > (mixdowns[1].duration || 0) ? 0 : 1;
      setCurrTime(mixdowns[longestMixdown].howl?.seek() || 0);
    }, 200);

    return () => clearInterval(interval);
  }, [playing]);

  const handleMixdownSelect = (index: number) => {
    const selectedMixdown = mixdowns[index];
    mixdowns.forEach((mixdown) => {
      if (mixdown.id === selectedMixdown.id) {
        mixdown.howl?.volume(1);
      } else {
        mixdown.howl?.volume(0);
      }
    });
    setMixdowns((prev) =>
      prev.map((prevMixdown, i) => ({
        ...prevMixdown,
        selected: i === index,
      }))
    );
  };

  function handlePlayPause() {
    if (playing) {
      mixdowns.forEach((mixdown) => mixdown.howl?.pause());
    } else {
      mixdowns.forEach((mixdown) => {
        mixdown.howl?.seek(currTime);
        if (!mixdown.howl?.playing()) mixdown.howl?.play();
      });
    }
    setPlaying(!playing);
  }

  const seek = (time: number) => {
    setCurrTime(time);
    mixdowns.forEach((mixdown) => {
      mixdown.howl?.seek(time);
      if (playing && !mixdown.howl?.playing()) mixdown.howl?.play();
    });
  };

  return (
    <div
      data-quick-assist-id="mixdown-compare"
      className="rounded mb-6 overflow-hidden relative"
    >
      <Button
        leftIcon={<ChevronLeft className="w-3 h-3" />}
        variant="light"
        color="gray"
        size="xs"
        className="absolute right-2 top-2 z-30"
        onClick={onClose}
      >
        Back
      </Button>

      <div>
        {mixdowns.map((mixdown, index) => (
          <MixdownComponent
            {...mixdown}
            key={mixdown.id}
            index={index}
            duration={duration}
            currentTime={currTime}
            playing={playing}
            onSelect={() => handleMixdownSelect(index)}
            onSeek={seek}
          />
        ))}
      </div>

      <Card className="relative z-20 flex items-center justify-center rounded-tr-none rounded-tl-none p-2">
        <div className="absolute flex items-center gap-2 left-2">
          <Input
            size="xs"
            classNames={{ input: "w-24 text-md text-center bg-dark-900" }}
            value={formatElapsedOverTotal(currTime, duration)}
          />
        </div>

        <ActionIcon
          data-testid="mixdown-compare-play-pause"
          variant={playing ? "light" : "filled"}
          color={playing ? "gray" : "primary"}
          onClick={handlePlayPause}
        >
          {playing ? <PauseRounded /> : <PlayArrowRounded />}
        </ActionIcon>
      </Card>
    </div>
  );
};

const MixdownComponent: React.FC<
  MixdownCompare & {
    index: number;
    duration: number;
    currentTime: number;
    playing: boolean;
    onSelect: (index: number) => void;
    onSeek: (time: number) => void;
  }
> = ({
  name,
  file,
  index,
  selected,
  duration,
  currentTime,
  playing,
  onSelect,
  onSeek,
}) => {
  const theme = useMantineTheme();

  const fromColor = index % 2 === 0 ? "indigo" : "purple";
  const toColor = index % 2 === 0 ? "rose" : "pink";

  return (
    <div
      className="relative p-1 shadow-2xl"
      style={{
        background: `linear-gradient(90deg, ${theme.colors[fromColor][7]}, ${theme.colors[toColor][7]})`,
        opacity: selected ? 1 : 0.5,
      }}
      onClick={() => onSelect(index)}
    >
      <p className="absolute left-2 top-2 font-semibold z-10 pointer-events-none">
        {name}
      </p>

      {file && (
        <div className="m-2">
          <WaveformLane
            file={file}
            currentTime={currentTime}
            playing={playing}
            duration={duration}
            height={stemHeight}
            // The compare view's drag is a plain scrub — never a region
            // creation like the project page's lane — so the default
            // fallback (onSeek called continuously while held) is enough.
            // It is gated on `selected` so dragging the unselected/muted
            // lane never moves playback, matching the old useDrag guard
            // (`if (!down || !selected) return;`).
            onSeek={(time) => {
              if (selected) onSeek(time);
            }}
          />
        </div>
      )}
    </div>
  );
};
